// src/shared/utils/MarkdownInsert.ts

/**
 * Result of a markdown insertion operation.
 * Contains the new text value and cursor position to restore.
 */
export interface InsertResult {
  value: string;
  selectionStart: number;
  selectionEnd: number;
}

/**
 * Static utility class for markdown syntax insertion.
 * All methods are pure functions that compute new values from textarea state.
 */
export class MarkdownInsert {
  /**
   * Wraps the selected text with before/after strings.
   * If no selection, inserts placeholder text between the markers.
   */
  static wrapSelection(
    value: string,
    selectionStart: number,
    selectionEnd: number,
    before: string,
    after: string,
    placeholder: string = 'text'
  ): InsertResult {
    const selectedText = value.substring(selectionStart, selectionEnd);
    const hasSelection = selectionStart !== selectionEnd;

    const insertText = hasSelection ? selectedText : placeholder;
    const newValue =
      value.substring(0, selectionStart) +
      before +
      insertText +
      after +
      value.substring(selectionEnd);

    // Position cursor to select the inserted/wrapped text
    const newStart = selectionStart + before.length;
    const newEnd = newStart + insertText.length;

    return { value: newValue, selectionStart: newStart, selectionEnd: newEnd };
  }

  /**
   * Inserts a prefix at the start of the current line.
   * Used for bullet lists, headings, etc.
   */
  static insertLinePrefix(
    value: string,
    selectionStart: number,
    prefix: string
  ): InsertResult {
    // Find the start of the current line
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;

    const newValue =
      value.substring(0, lineStart) + prefix + value.substring(lineStart);

    // Move cursor after the prefix
    const newPos = selectionStart + prefix.length;

    return { value: newValue, selectionStart: newPos, selectionEnd: newPos };
  }

  /**
   * Inserts a markdown link.
   * If text is selected, uses it as the link text.
   * Otherwise inserts [link text](url) with 'url' selected for easy replacement.
   */
  static insertLink(
    value: string,
    selectionStart: number,
    selectionEnd: number
  ): InsertResult {
    const selectedText = value.substring(selectionStart, selectionEnd);
    const hasSelection = selectionStart !== selectionEnd;

    if (hasSelection) {
      // Wrap selected text as link text, position cursor in URL placeholder
      const linkText = `[${selectedText}](url)`;
      const newValue =
        value.substring(0, selectionStart) + linkText + value.substring(selectionEnd);

      // Select 'url' for easy replacement
      const urlStart = selectionStart + selectedText.length + 3; // after '[text]('
      const urlEnd = urlStart + 3; // 'url'.length

      return { value: newValue, selectionStart: urlStart, selectionEnd: urlEnd };
    }

    // No selection: insert full placeholder, select 'link text'
    const linkText = '[link text](url)';
    const newValue =
      value.substring(0, selectionStart) + linkText + value.substring(selectionEnd);

    // Select 'link text' for easy replacement
    const textStart = selectionStart + 1; // after '['
    const textEnd = textStart + 9; // 'link text'.length

    return { value: newValue, selectionStart: textStart, selectionEnd: textEnd };
  }

  /**
   * Applies bold formatting: **text**
   */
  static bold(value: string, selectionStart: number, selectionEnd: number): InsertResult {
    return this.wrapSelection(value, selectionStart, selectionEnd, '**', '**', 'bold text');
  }

  /**
   * Applies italic formatting: *text*
   */
  static italic(value: string, selectionStart: number, selectionEnd: number): InsertResult {
    return this.wrapSelection(value, selectionStart, selectionEnd, '*', '*', 'italic text');
  }

  /**
   * Inserts bullet list items.
   * If multiple lines are selected, prefixes each line with `- `.
   * Otherwise prefixes the current line.
   */
  static bulletList(
    value: string,
    selectionStart: number,
    selectionEnd: number
  ): InsertResult {
    // Find the start of the first selected line
    const firstLineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    // Find the end of the last selected line
    let lastLineEnd = value.indexOf('\n', selectionEnd);
    if (lastLineEnd === -1) lastLineEnd = value.length;

    // Extract the selected lines
    const selectedBlock = value.substring(firstLineStart, lastLineEnd);
    const lines = selectedBlock.split('\n');

    // Prefix each line with `- `
    const prefixedLines = lines.map((line) => '- ' + line);
    const newBlock = prefixedLines.join('\n');

    const newValue =
      value.substring(0, firstLineStart) + newBlock + value.substring(lastLineEnd);

    // Position cursor at end of the transformed block
    const newEnd = firstLineStart + newBlock.length;

    return { value: newValue, selectionStart: firstLineStart, selectionEnd: newEnd };
  }
}
